import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	SettingsSaveModel,
	AiAssistantSettingsSaveModel,
	getAiAssistantSettings,
	saveAiAssistantSettings,
	SettingsViewModel
} from "client/Api/AIAssistantSettings/aIAssistantSettings";
import { IFormHandler, useFormHandler } from "client/Forms";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { PermissionsUnionModel } from "client/Api/common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	Checkbox,
	Form,
	FormItem,
	Tabs,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { WhoHasAccessSection } from "./WhoHasAccessSection";
import { UsageSection } from "./UsageSection";
import { useProvisionKellyHelpsDialog } from "./ProvisionKellyHelpsDialog";
import { ActionKind } from "client_legacy/ndaw/actions";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { isNil, isNilOrWhiteSpace } from "client/Misc/utils";
import { IValidationConfiguration } from "client/Validation/builder";
import { IAggregateAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { deepComparison } from "client/Misc/compareUtils";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";

export const AiAssistantSettingsPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormModel>(EMPTY_FORM);
	const [state, setState] = useState<State>(EMPTY_STATE);
	const [activeTabId, setActiveTabId] = useState<TabId>(usageTabId);

	const {
		initialModel,
		isProvisioned,
		kellyHelpsUrl,
		usersAndUserGroupsData
	} = state;

	const formHandler: IFormHandler<FormModel> = useFormHandler<FormModel>(
		formData,
		setFormData,
		validationConfiguration
	);

	useActiveSettingsMenuItem(SettingsPageKind.AiAssistant);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [provisioningDialog, openProvisioning] = useProvisionKellyHelpsDialog();

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const {
						isProvisioned,
						kellyHelpsUrl,
						settings,
						usersAndUserGroupsData
					} = await getAiAssistantSettings(signal)
						.catch(handlePageDataLoadingError);

					const formModel: FormModel = convertToSaveModel(settings);

					setFormData(formModel);

					setState({
						initialModel: formModel,
						isProvisioned,
						kellyHelpsUrl,
						usersAndUserGroupsData
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			setIsBusy(true);

			try {
				const validation: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validation.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const result: AiAssistantSettingsSaveModel = prepareForSaving(formData);

				await saveAiAssistantSettings(
					result,
					globalCancellation
				);

				alertManager.showSuccess(localize("_ChangesSaved"));

				setState(x => ({
					...x,
					initialModel: formData
				}));

				if (!deepComparison(initialModel, formData)) {
					reloadCurrentPage();
				} else {
					setIsBusy(false);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler,
			formData,
			globalCancellation,
			initialModel
		]
	);

	const cancelHandler = useCallback(
		(): void => {
			setFormData(initialModel);
			formHandler
				.validation
				.reset();
		},
		[
			formHandler.validation,
			initialModel
		]
	);

	const enabledChangeHandler = useCallback(
		async (isEnabled: boolean): Promise<void> => {
			if (!isEnabled) {
				setActiveTabId(usageTabId);
				return;
			}

			if (isProvisioned) {
				setActiveTabId(whoHasAccessTabId);
				return;
			}

			const revertToDisabled = (): void => {
				const attachable: IValueInputAttachable<boolean> = formHandler.for("isEnabled");

				attachable.reducer
					.setValue(false);

				attachable.validation
					.triggerInputFinished();
			};

			if (isNilOrWhiteSpace(userConfiguration().emailAddress)) {
				alertManager.showError(localize("_AiAssistantRequiresEmailAddressInfo"));
				revertToDisabled();
				return;
			}

			const provisioning: TryResult<void> = await openProvisioning({ kellyHelpsUrl });

			if (!provisioning.wasSuccessful) {
				revertToDisabled();
				return;
			}

			setState(x => ({
				...x,
				isProvisioned: true
			}));

			setActiveTabId(whoHasAccessTabId);
		},
		[
			alertManager,
			formHandler,
			isProvisioned,
			kellyHelpsUrl,
			openProvisioning
		]
	);

	const actions = useMemo(
		(): readonly ToolbarAction<undefined>[] => getActions(
			cancelHandler,
			submitHandler
		),
		[
			submitHandler,
			cancelHandler
		]
	);

	const whoHasAccessAttachable: IValueInputAttachable<PermissionsUnionModel> = formHandler
		.for("settings")
		.for("value")
		.for("whoHasAccess");

	const whoHasAccessAggregate: IAggregateAttachable = formHandler
		.for("settings")
		.for("value")
		.under("whoHasAccess");

	const isConfiguring: boolean = formData.isEnabled && isNil(provisioningDialog);
	const canShowUsage: boolean = isProvisioned && isNil(provisioningDialog);

	return (
		<SettingsPage
			title={localize("_AiAssistant")}
			hasBusyIndicator={isBusy}
			actions={actions}
			actionContext={undefined}
		>
			<SettingsPage.Section height="stretch">
				<Form
					onSubmit={submitHandler}
					height="fill"
					trackingId="ai-assistant-settings-form"
				>
					<FormItem>
						<Checkbox
							{...formHandler.for("isEnabled")
								.attachToCheckbox()
								.withOnChange(enabledChangeHandler)
							}
							text={localize("_EnableAIAssistant")}
						/>
						<ValidationError {...formHandler.for("isEnabled").attachToValidationError()} />
					</FormItem>
					{canShowUsage && (
						<Tabs<TabId>
							height="fill"
							activeTabId={activeTabId}
							onActiveTabIdChange={setActiveTabId}
						>
							<Tabs.Tab
								id={usageTabId}
								title={localize("_Usage")}
							>
								<UsageSection kellyHelpsUrl={kellyHelpsUrl} />
							</Tabs.Tab>
							{isConfiguring && (
								<Tabs.Tab
									{...whoHasAccessAggregate.attachToTab()}
									id={whoHasAccessTabId}
									title={localize("_WhoHasAccess")}
								>
									<WhoHasAccessSection
										attachable={whoHasAccessAttachable}
										usersAndUserGroupsData={usersAndUserGroupsData}
									/>
								</Tabs.Tab>
							)}
						</Tabs>
					)}
				</Form>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
			{provisioningDialog}
		</SettingsPage>
	);
};
AiAssistantSettingsPage.displayName = "AiAssistantSettingsPage";

type TabId =
	| typeof usageTabId
	| typeof whoHasAccessTabId;

const usageTabId = "usage";
const whoHasAccessTabId = "who-has-access";

type FormModel = OverrideProperties<
	AiAssistantSettingsSaveModel,
	{
		settings: SetOptionalModel<SettingsSaveModel>;
	}
> & {
	isEnabled: boolean;
};

const EMPTY_FORM: FormModel = {
	isEnabled: false,
	settings: {
		isSet: true,
		value: {
			whoHasAccess: {
				everyone: false,
				specificSet: {
					userGroupIds: [],
					userIds: []
				}
			}
		}
	}
};

const validationConfiguration: IValidationConfiguration<FormModel> =
	createValidationConfiguration<FormModel>();

type State = {
	initialModel: FormModel;
	isProvisioned: boolean;
	kellyHelpsUrl: string;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

const EMPTY_STATE: State = {
	initialModel: EMPTY_FORM,
	isProvisioned: false,
	kellyHelpsUrl: "",
	usersAndUserGroupsData: {
		userGroups: [],
		users: []
	}
};

function convertToSaveModel(
	settings: OptionalModel<SettingsViewModel>
): FormModel {
	if (!settings.isSet) {
		return EMPTY_FORM;
	}

	const {
		whoHasAccess
	} = settings.value;

	return {
		isEnabled: true,
		settings: {
			isSet: true,
			value: {
				whoHasAccess: {
					everyone: whoHasAccess.everyone,
					specificSet: !whoHasAccess.everyone
						? whoHasAccess.specificSet
						: {
							userGroupIds: [],
							userIds: []
						}
				}
			}
		}
	};
}

function prepareForSaving(
	model: FormModel
): AiAssistantSettingsSaveModel {
	const {
		isEnabled,
		settings
	} = model;

	const {
		whoHasAccess
	} = settings.value;

	return {
		settings: isEnabled
			? {
				isSet: true,
				value: {
					whoHasAccess: {
						everyone: whoHasAccess.everyone,
						specificSet: !whoHasAccess.everyone
							? whoHasAccess.specificSet
							: null
					}
				}
			}
			: { isSet: false }
	};
}

function getActions(
	cancel: () => void,
	save: () => void
): readonly ToolbarAction<undefined>[] {
	return [
		{
			kind: ActionKind.Regular,
			title: localize("_Save"),
			name: "save",
			canExecute: () => true,
			execute: save,
			icon: "floppy-disk",
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			title: localize("_Cancel"),
			name: "cancel",
			canExecute: () => true,
			execute: cancel,
			icon: "xmark"
		}
	];
}

export const AiAssistantSettingsRouting = {
	page: "AiAssistantSettings"
};
